/**
 * Enhanced invite system for mrmr.
 *
 * Handles meeting code generation/validation, room ID derivation via
 * HMAC-SHA256, URL fragment parsing for event links and live links,
 * and peer address encoding/decoding for late joiner hints.
 */

const CHARS = 'abcdefghijklmnopqrstuvwxyz'

/** Code format regex: exactly 3 groups of 4 lowercase letters separated by dashes */
const CODE_REGEX = /^[a-z]{4}-[a-z]{4}-[a-z]{4}$/

/** HMAC label for room ID derivation -- cryptographically independent from signaling key */
const ROOM_ID_LABEL = 'mrmr-room-v1'

/** HMAC label for signaling encryption key derivation */
const SIGNALING_KEY_LABEL = 'mrmr-signaling-v1'

/** Compact peer address for live link encoding */
export interface CompactPeerAddress {
  /** Trystero selfId prefix (first 8 chars) */
  id: string
  /** DTLS fingerprint prefix (first 8 bytes, base64url-encoded) */
  fp: string
}

/** Result of parsing a meeting URL fragment */
export interface ParsedMeetingUrl {
  /** The meeting code in display format (xxxx-xxxx-xxxx) */
  code: string
  /** Decoded peer addresses from live link, or null for event links */
  peers: CompactPeerAddress[] | null
}

/**
 * Generate a random 12-character meeting code formatted as xxxx-xxxx-xxxx.
 * Uses crypto.getRandomValues for secure randomness.
 */
export function generateMeetingCode(): string {
  const bytes = new Uint8Array(12)
  crypto.getRandomValues(bytes)
  let code = ''
  for (let i = 0; i < 12; i++) {
    if (i > 0 && i % 4 === 0) code += '-'
    code += CHARS[bytes[i] % 26]
  }
  return code
}

/**
 * Format raw user input into a meeting code with dashes.
 * Strips non-alpha characters, lowercases, and limits to 12 chars.
 */
export function formatCodeInput(raw: string): string {
  const clean = raw.toLowerCase().replace(/[^a-z]/g, '').slice(0, 12)
  const parts: string[] = []
  for (let i = 0; i < clean.length; i += 4) {
    parts.push(clean.slice(i, i + 4))
  }
  return parts.join('-')
}

/**
 * Validate that a string is a properly formatted meeting code.
 */
export function isValidCode(code: string): boolean {
  return CODE_REGEX.test(code)
}

/**
 * Convert a meeting code to its canonical form (no dashes, lowercase).
 */
export function canonicalize(code: string): string {
  return code.replace(/-/g, '').toLowerCase()
}

/**
 * Derive a room ID from a meeting code using HMAC-SHA256.
 * The meeting code is the HMAC key, and 'mrmr-room-v1' is the message.
 * Returns a 64-character hex string.
 */
export async function deriveRoomId(meetingCode: string): Promise<string> {
  const canonical = canonicalize(meetingCode)
  const encoder = new TextEncoder()
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(canonical),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
  const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(ROOM_ID_LABEL))
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Derive a signaling encryption key from a meeting code using HMAC-SHA256.
 * Uses a different label than room ID derivation for cryptographic independence.
 * Returns a 64-character hex string.
 */
export async function deriveSignalingKey(meetingCode: string): Promise<string> {
  const canonical = canonicalize(meetingCode)
  const encoder = new TextEncoder()
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(canonical),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
  const sig = await crypto.subtle.sign(
    'HMAC',
    key,
    encoder.encode(SIGNALING_KEY_LABEL),
  )
  return Array.from(new Uint8Array(sig).slice(0, 32))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Generate an event link (pre-meeting) for sharing.
 * The meeting code is placed in the URL fragment so the server never sees it.
 */
export function generateEventLink(code: string): string {
  return `${window.location.origin}${window.location.pathname}#${code}`
}

/**
 * Generate a live link (mid-meeting) that includes peer address hints.
 * The peer data is encoded in the fragment after a ? separator.
 */
export function generateLiveLink(
  code: string,
  peers: CompactPeerAddress[],
): string {
  const peerData = encodePeerAddresses(peers)
  return `${window.location.origin}${window.location.pathname}#${code}?p=${peerData}`
}

/**
 * Encode peer addresses into a compact base64url string.
 * Each peer is packed as: 8-byte id prefix + 8-byte fingerprint prefix = 16 bytes.
 */
export function encodePeerAddresses(peers: CompactPeerAddress[]): string {
  const buf = new Uint8Array(peers.length * 16)
  const encoder = new TextEncoder()

  for (let i = 0; i < peers.length; i++) {
    const offset = i * 16
    // Encode id (first 8 chars as ASCII bytes)
    const idBytes = encoder.encode(peers[i].id.slice(0, 8))
    buf.set(idBytes.slice(0, 8), offset)
    // Encode fingerprint prefix (8 bytes from base64url)
    const fpBytes = base64urlDecode(peers[i].fp).slice(0, 8)
    buf.set(fpBytes, offset + 8)
  }

  return base64urlEncode(buf)
}

/**
 * Decode peer addresses from a compact base64url string.
 */
export function decodePeerAddresses(data: string): CompactPeerAddress[] {
  const buf = base64urlDecode(data)
  const peers: CompactPeerAddress[] = []
  const decoder = new TextDecoder()

  for (let i = 0; i + 16 <= buf.length; i += 16) {
    const idBytes = buf.slice(i, i + 8)
    const fpBytes = buf.slice(i + 8, i + 16)
    peers.push({
      id: decoder.decode(idBytes).replace(/\0+$/, ''),
      fp: base64urlEncode(fpBytes),
    })
  }

  return peers
}

/**
 * Parse a URL hash into a meeting code and optional peer addresses.
 * Handles:
 *   - Event links: #wolf-mesa-pyre
 *   - Live links:  #wolf-mesa-pyre?p=<base64url>
 *   - Full URLs:   https://mrmr.network/#wolf-mesa-pyre
 *   - Fragment-only: #wolf-mesa-pyre
 *   - Bare codes:  wolf-mesa-pyre
 *
 * Returns null if the hash does not contain a valid meeting code.
 */
export function parseMeetingUrl(hash: string): ParsedMeetingUrl | null {
  if (!hash) return null

  // If it looks like a full URL, extract the fragment
  let fragment = hash
  if (fragment.includes('#')) {
    fragment = fragment.split('#').pop() ?? ''
  }
  // Remove leading # if still present
  if (fragment.startsWith('#')) {
    fragment = fragment.slice(1)
  }

  if (!fragment) return null

  // Split on '?' to separate code from params
  const qIndex = fragment.indexOf('?')
  const codePart = qIndex === -1 ? fragment : fragment.slice(0, qIndex)
  const paramsPart = qIndex === -1 ? null : fragment.slice(qIndex + 1)

  // Validate code format
  if (!CODE_REGEX.test(codePart)) return null

  // Parse peer addresses from live link
  let peers: CompactPeerAddress[] | null = null
  if (paramsPart) {
    const params = new URLSearchParams(paramsPart)
    const peerData = params.get('p')
    if (peerData) {
      try {
        peers = decodePeerAddresses(peerData)
      } catch {
        // Invalid peer data -- treat as event link
        peers = null
      }
    }
  }

  return { code: codePart, peers }
}

/**
 * Determine the route from a URL hash fragment.
 * - No hash or empty: 'landing'
 * - Valid meeting code: 'lobby'
 * - Invalid format: 'error'
 */
export function routeFromHash(
  hash: string,
): 'landing' | 'lobby' | 'error' {
  if (!hash || hash === '#' || hash === '') return 'landing'
  const parsed = parseMeetingUrl(hash)
  if (parsed) return 'lobby'
  return 'error'
}

// --- Base64url helpers ---

function base64urlEncode(data: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64urlDecode(str: string): Uint8Array {
  // Restore standard base64
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
  // Add padding
  while (base64.length % 4 !== 0) {
    base64 += '='
  }
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}
